import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  Copy,
  Mail,
  Pencil,
  Plus,
  Search,
  X,
} from "lucide-react";
import type { PaymentPayee, PaymentRecord } from "./types";
import { payeeSearchScore, paymentQuote } from "./payment-pricing";

const money = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
    cents / 100,
  );

export function PaymentInstructionsEditor({
  payees,
  payee,
  onPayee,
  cashOnly,
  onCashOnly,
  electronicHst,
  onElectronicHst,
  locked = false,
}: {
  payees: PaymentPayee[];
  payee: PaymentRecord["payee"];
  onPayee: (value: PaymentRecord["payee"]) => void;
  cashOnly: boolean;
  onCashOnly: (value: boolean) => void;
  electronicHst: boolean;
  onElectronicHst: (value: boolean) => void;
  locked?: boolean;
}) {
  const [saved, setSaved] = useState(payees);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Partial<PaymentPayee>>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    let active = true;
    void fetch("/api/teacher/payment-payees")
      .then(async (response) => {
        if (!response.ok)
          throw new Error(
            "Could not load saved payees. Close and reopen to retry.",
          );
        const items = (await response.json()) as PaymentPayee[];
        if (active) setSaved(items);
      })
      .catch((cause) => {
        if (active) setError(cause.message);
      });
    return () => {
      active = false;
    };
  }, [open]);
  const matches = useMemo(
    () =>
      saved
        .filter((item) => !item.archived)
        .map((item) => ({
          item,
          score: payeeSearchScore(query, item.name, item.email),
        }))
        .filter(({ score }) => Number.isFinite(score))
        .sort(
          (a, b) => a.score - b.score || a.item.name.localeCompare(b.item.name),
        ),
    [saved, query],
  );
  const save = async (archive = false) => {
    if (!editing || busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/teacher/payment-payees", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...editing, archived: archive }),
      });
      const value = await response.json();
      if (!response.ok) throw new Error(value.error || "Could not save payee");
      const record = value as PaymentPayee;
      setSaved((items) => [
        ...items.filter((item) => item.id !== record.id),
        record,
      ]);
      if (!archive) {
        onPayee({ id: record.id, name: record.name, email: record.email });
        setOpen(false);
      }
      setEditing(undefined);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save payee");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="payment-instructions-editor">
      <header>
        <span className="section-label">How to pay</span>
        <p>Clear instructions, private to this Member.</p>
      </header>
      <div
        className="payment-method-choices"
        aria-label="Accepted payment methods"
      >
        {[false, true].map((cash) => (
          <button
            key={String(cash)}
            type="button"
            className={`payment-method-choice ${cashOnly === cash ? "selected" : ""}`}
            aria-pressed={cashOnly === cash}
            disabled={locked}
            onClick={() => {
              onCashOnly(cash);
              if (cash) onPayee(null);
            }}
          >
            <span>
              <b>{cash ? "Cash only" : "Cash & e-transfer"}</b>
              <small>
                {cash
                  ? "No electronic payment option"
                  : "Show both ways to pay"}
              </small>
            </span>
            {cashOnly === cash && <Check />}
          </button>
        ))}
      </div>
      {!cashOnly && (
        <>
          {!locked && (
            <button
              type="button"
              role="switch"
              aria-checked={electronicHst}
              aria-label="Add HST (13%) to e-transfer"
              disabled={locked}
              className="payment-tax-switch"
              onClick={() => onElectronicHst(!electronicHst)}
            >
              <span>
                <b>Include 13% HST for e-transfer</b>
                <small>
                  {electronicHst
                    ? "Members see the electronic total first, with a tax breakdown."
                    : "No additional tax. Members see the original amount."}
                </small>
              </span>
              <span className="switch-track">
                <span />
              </span>
            </button>
          )}
          <div className="payee-control">
            <span className="field-label">
              E-transfer recipient <small>Optional</small>
            </span>
            <button
              type="button"
              className="payee-trigger"
              aria-expanded={open}
              onClick={() => {
                setOpen(!open);
                setEditing(undefined);
                setError("");
              }}
            >
              <span className="payee-icon">
                <Mail />
              </span>
              <span>
                <b>{payee?.name || "Choose a saved payee"}</b>
                <small>
                  {payee?.email ||
                    "Search by name or email, or add someone new"}
                </small>
              </span>
              <ChevronDown />
            </button>
            {payee && (
              <button
                type="button"
                className="text-button payee-clear"
                onClick={() => onPayee(null)}
              >
                <X /> Remove recipient from this payment
              </button>
            )}
            {open && (
              <div className="payee-panel">
                {editing ? (
                  <div className="payee-editor">
                    <header>
                      <b>
                        {editing.id ? "Edit saved payee" : "Save a new payee"}
                      </b>
                      <button
                        type="button"
                        className="icon-button"
                        aria-label="Cancel payee editing"
                        onClick={() => setEditing(undefined)}
                      >
                        <X />
                      </button>
                    </header>
                    <label>
                      <span>Payee name</span>
                      <input
                        autoFocus
                        value={editing.name ?? ""}
                        maxLength={100}
                        placeholder="Avante Dance Company"
                        onChange={(event) =>
                          setEditing({ ...editing, name: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      <span>Payee email</span>
                      <input
                        type="email"
                        value={editing.email ?? ""}
                        maxLength={254}
                        placeholder="payments@example.com"
                        onChange={(event) =>
                          setEditing({ ...editing, email: event.target.value })
                        }
                      />
                    </label>
                    <small>
                      Saved for all Coaches. Existing payment recipients stay
                      unchanged.
                    </small>
                    <div className="payee-editor-actions">
                      <button
                        type="button"
                        className="primary-button"
                        disabled={busy}
                        onClick={() => void save()}
                      >
                        {busy ? "Saving…" : "Save and use payee"}
                      </button>
                      {editing.id && (
                        <button
                          type="button"
                          className="text-button"
                          disabled={busy}
                          onClick={() => void save(true)}
                        >
                          Archive saved payee
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="people-search">
                      <Search />
                      <span className="sr-only">Find a payee</span>
                      <input
                        autoFocus
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Find a name or email…"
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            event.stopPropagation();
                            setOpen(false);
                          }
                          if (event.key === "ArrowDown") {
                            event.preventDefault();
                            event.currentTarget
                              .closest(".payee-panel")
                              ?.querySelector<HTMLButtonElement>(
                                ".payee-result button",
                              )
                              ?.focus();
                          }
                        }}
                      />
                    </label>
                    <div className="payee-results" aria-label="Saved payees">
                      {matches.map(({ item }) => (
                        <div
                          key={item.id}
                          className={`payee-result ${payee?.id === item.id ? "selected" : ""}`}
                        >
                          <button
                            type="button"
                            aria-pressed={payee?.id === item.id}
                            onClick={() => {
                              onPayee({
                                id: item.id,
                                name: item.name,
                                email: item.email,
                              });
                              setOpen(false);
                            }}
                          >
                            <span className="payee-avatar">
                              {item.name.slice(0, 2).toUpperCase()}
                            </span>
                            <span>
                              <b>{item.name}</b>
                              <small>{item.email}</small>
                            </span>
                            {payee?.id === item.id && <Check />}
                          </button>
                          <button
                            type="button"
                            className="icon-button"
                            aria-label={`Edit payee ${item.name}`}
                            onClick={() => setEditing(item)}
                          >
                            <Pencil />
                          </button>
                        </div>
                      ))}
                    </div>
                    {!matches.length && (
                      <p className="payee-empty">
                        {saved.some((item) => !item.archived)
                          ? "No matching payees. Try a name or part of an email."
                          : "Save your first payee to reuse their details next time."}
                      </p>
                    )}
                    <button
                      type="button"
                      className="payee-add"
                      onClick={() => {
                        setEditing({
                          name: query.includes("@") ? "" : query,
                          email: query.includes("@") ? query : "",
                        });
                        setError("");
                      }}
                    >
                      <Plus /> Add a payee
                    </button>
                  </>
                )}
                {error && (
                  <p role="alert" className="auth-message">
                    {error}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {locked && (
        <small>
          Payment methods and pricing stay fixed once money has been recorded.
          You can still update notes and the recipient.
        </small>
      )}
    </section>
  );
}

export function MemberPaymentInstructions({
  payment,
}: {
  payment: PaymentRecord;
}) {
  const quote = paymentQuote(payment);
  const [copyStatus, setCopyStatus] = useState("");
  const due = payment.status === "due";
  const paid = payment.paidCents ?? 0;
  return (
    <section className="member-payment-instructions">
      {due && (
        <div className="payment-price-options">
          <div className="payment-price-primary">
            <span className="section-label">
              {payment.settlementPricing?.method === "cash" || payment.cashOnly
                ? "Cash"
                : "E-transfer"}
              {quote.fixed && paid > 0 ? " · Remaining" : ""}
            </span>
            <strong>
              {money(
                Math.max(
                  0,
                  (payment.cashOnly ? quote.cashCents : quote.electronicCents) -
                    paid,
                ),
              )}
            </strong>
            <small>
              {quote.hstCents > 0
                ? `${money(quote.baseCents)} + HST (13%) ${money(quote.hstCents)}`
                : quote.fixed
                  ? "Recorded payment total"
                  : "No additional tax"}
            </small>
          </div>
          {!payment.cashOnly && !quote.fixed && (
            <div className="payment-price-cash">
              <span>Prefer cash?</span>
              <b>{money(quote.cashCents)}</b>
              <small>Cash amount</small>
            </div>
          )}
          {payment.cashOnly && (
            <span className="payment-cash-only">Cash only</span>
          )}
        </div>
      )}
      {!payment.cashOnly &&
        payment.settlementPricing?.method !== "cash" &&
        payment.payee && (
          <div className="member-payee">
            <span className="payee-icon">
              <Mail />
            </span>
            <span>
              <small>{due ? "Send e-transfer to" : "Recipient"}</small>
              <b>{payment.payee.name}</b>
              <span>{payment.payee.email}</span>
            </span>
            <button
              type="button"
              className="icon-button"
              aria-label={`Copy email for ${payment.payee.name}`}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(payment.payee!.email);
                  setCopyStatus("Email copied");
                } catch {
                  setCopyStatus("Could not copy. Select the email to copy it.");
                }
              }}
            >
              <Copy />
            </button>
          </div>
        )}
      {copyStatus && <small role="status">{copyStatus}</small>}
    </section>
  );
}
